import React, { memo, useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { ButtonGroupWrapper } from '@sharedCR/coreDesignSystem/Buttons';
import FileUploadCDS from '@sharedCR/coreDesignSystem/FileUploadCDS';
import { importMismoApi } from 'app/services/api';
import PropTypes from 'prop-types';

import { HORIZONTAL_ALIGNMENT } from 'constants/ui';

import { closeDialogAction, showErrorMessageAction } from 'store/global/actions';

import MismoChangeSectionGroup from './components/MismoChangeSectionGroup';
import MismoChangeSidebar from './components/MismoChangeSidebar';
import {
  ACCEPTED_FILE_TYPES,
  NO_CHANGES_MESSAGE,
  PREVIEW_FAILED_MESSAGE,
  PreviewResultType,
  UPLOAD_CTA_MESSAGE,
} from './ImportMismoIntoLoanModal.constants';
import {
  changesScrollStyles,
  footerStyles,
  modalBodyStyles,
  previewStyles,
} from './ImportMismoIntoLoanModal.styles';
import { ImportMismoIntoLoanModalTestIds } from './ImportMismoIntoLoanModal.testIds';
import { buildMismoChangeSections, buildMismoFormData } from './ImportMismoIntoLoanModal.utils';

const ImportMismoIntoLoanModal = ({ dialogId, applicationId, onProceed }) => {
  const dispatch = useDispatch();

  const sectionRefs = useRef({});

  const [selectedFile, setSelectedFile] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isProceeding, setIsProceeding] = useState(false);
  const [selectedSectionIndex, setSelectedSectionIndex] = useState(0);

  const onClose = useCallback(() => dispatch(closeDialogAction(dialogId)), [dispatch, dialogId]);

  const onFileSelected = useCallback(
    async file => {
      setSelectedFile(file);
      setPreview(null);
      setSelectedSectionIndex(0);

      if (!file) {
        return;
      }

      setIsLoading(true);

      try {
        const { data } = await importMismoApi.previewApplicationUpdate({
          applicationId,
          formData: buildMismoFormData(file),
        });

        if (data?.type !== PreviewResultType.SUCCESS) {
          setErrorMessage(data?.error ?? PREVIEW_FAILED_MESSAGE);

          return;
        }

        setPreview(data);
      } catch (error) {
        setErrorMessage(error?.response?.data?.message ?? error?.message ?? PREVIEW_FAILED_MESSAGE);
      } finally {
        setIsLoading(false);
      }
    },
    [applicationId],
  );

  const onProceedClick = useCallback(async () => {
    if (!preview?.stagingId) {
      return;
    }

    setIsProceeding(true);

    try {
      await onProceed(preview.stagingId);
      onClose();
    } catch (error) {
      dispatch(
        showErrorMessageAction({
          message: error?.response?.data?.message ?? error?.message ?? PREVIEW_FAILED_MESSAGE,
        }),
      );
      setIsProceeding(false);
    }
  }, [dispatch, onClose, onProceed, preview]);

  const registerSectionRef = useCallback(
    index => element => {
      sectionRefs.current[index] = element;
    },
    [],
  );

  const onSectionSelect = useCallback(index => {
    setSelectedSectionIndex(index);
    sectionRefs.current[index]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const changeSections = buildMismoChangeSections(preview);
  const hasChanges = changeSections.length > 0;
  const isProceedDisabled = !preview?.stagingId || isLoading || isProceeding;

  return (
    <Box sx={modalBodyStyles}>
      <FileUploadCDS
        acceptedFileTypes={ACCEPTED_FILE_TYPES}
        callToActionMessage={UPLOAD_CTA_MESSAGE}
        disabled={isLoading || isProceeding}
        errorMessage={errorMessage}
        isLoading={isLoading}
        selectedFile={selectedFile}
        setErrorMessage={setErrorMessage}
        setSelectedFile={onFileSelected}
      />

      {preview && (
        <Box data-testid={ImportMismoIntoLoanModalTestIds.PREVIEW} sx={previewStyles}>
          {hasChanges ? (
            <>
              <MismoChangeSidebar
                sections={changeSections}
                selectedIndex={selectedSectionIndex}
                onSelect={onSectionSelect}
              />
              <Box sx={changesScrollStyles}>
                {changeSections.map((changeSection, index) => (
                  <Box key={changeSection.section} ref={registerSectionRef(index)}>
                    <MismoChangeSectionGroup
                      changes={changeSection.changes}
                      section={changeSection.section}
                    />
                  </Box>
                ))}
              </Box>
            </>
          ) : (
            <Typography color="text.secondary" variant="regular14">
              {NO_CHANGES_MESSAGE}
            </Typography>
          )}
        </Box>
      )}

      <ButtonGroupWrapper alignment={HORIZONTAL_ALIGNMENT.RIGHT} sx={footerStyles}>
        <Button
          data-testid={ImportMismoIntoLoanModalTestIds.CANCEL_BUTTON}
          disabled={isProceeding}
          size="large"
          variant="line"
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          data-testid={ImportMismoIntoLoanModalTestIds.PROCEED_BUTTON}
          disabled={isProceedDisabled}
          size="large"
          onClick={onProceedClick}
        >
          {isProceeding ? <CircularProgress color="inherit" size={20} /> : 'Proceed'}
        </Button>
      </ButtonGroupWrapper>
    </Box>
  );
};

ImportMismoIntoLoanModal.propTypes = {
  dialogId: PropTypes.string.isRequired,
  applicationId: PropTypes.string.isRequired,
  onProceed: PropTypes.func.isRequired,
};

export default memo(ImportMismoIntoLoanModal);
